// Portal layouts ("themes"): the whole shell around the pages — navigation,
// brand, user block, theme toggle, sign-out. Chosen per client / reseller on
// the dashboard (branding.layout; keys match LAYOUTS in the API's
// helpers/clientBranding.js). Light/dark is separate and works in every layout.
//
//   classic  collapsible sidebar on the left (the original portal layout)
//   topbar   navigation across the top, full-width pages
//   rail     slim icon rail + header bar with the page title
//   panel    sidebar filled with the brand's primary colour
//   minimal  centred page, brand on top, pill-tab navigation
//
// Every shell takes the same props (see PortalShell) and renders `children`
// (banners + <Outlet />) as the page. This file is shared verbatim by the
// client portal and the reseller portal — keep the copies in sync.
import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { LogOut, Menu, X, PanelLeftOpen, PanelLeftClose } from 'lucide-react'
import '../Layout.css'
import './layouts.css'

export const LAYOUT_KEYS = ['classic', 'topbar', 'rail', 'panel', 'minimal']

// props:
//   layout       one of LAYOUT_KEYS (anything else → classic)
//   navItems     [{ path, icon, label, exact? }]
//   brandName    text next to the logo
//   logoSrc, onLogoError
//   user         { name, initials, sub, subClass } — sub is e.g. the balance
//   theme        { Icon, label, cycle }
//   sync         'connected' | 'reconnecting' | 'disconnected' | null (no dot)
//   onLogout
export default function PortalShell(props) {
  const Shell = SHELLS[props.layout] || ClassicShell
  return <Shell {...props} />
}

const syncTitle = { connected: 'Live sync active', reconnecting: 'Reconnecting...', disconnected: 'Offline' }
function SyncDot({ sync }) {
  if (!sync) return null
  return <span className={`sync-dot ${sync}`} title={syncTitle[sync]} />
}

function ThemeButton({ theme, className = 'theme-toggle-btn', showLabel = true }) {
  const Icon = theme.Icon
  return (
    <button type="button" className={className} onClick={theme.cycle} title={`Theme: ${theme.label}`}>
      <Icon size={18} />
      {showLabel && <span>{theme.label}</span>}
    </button>
  )
}

function LogoutButton({ onLogout, className = 'logout-btn', showLabel = true }) {
  return (
    <button type="button" className={className} onClick={onLogout} title="Sign Out">
      <LogOut size={18} />
      {showLabel && <span>Sign Out</span>}
    </button>
  )
}

// Label of the nav item for the current path (for header titles).
function useCurrentLabel(navItems) {
  const { pathname } = useLocation()
  const hit = [...navItems]
    .sort((a, b) => b.path.length - a.path.length)
    .find(i => (i.exact ? pathname === i.path : pathname === i.path || pathname.startsWith(i.path.replace(/\/$/, '') + '/')))
  return hit?.label || ''
}

// ---------------------------------------------------------------------------
// classic — the original collapsible sidebar (markup/classes from Layout.css)
// ---------------------------------------------------------------------------
function ClassicShell({ navItems, brandName, logoSrc, onLogoError, user, theme, sync, onLogout, children }) {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const sidebarVariants = {
    open: { width: 280, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
    closed: { width: 80, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } }
  }
  const labelVariants = {
    open: { opacity: 1, x: 0, display: 'block', transition: { delay: 0.1 } },
    closed: { opacity: 0, x: -10, transitionEnd: { display: 'none' } }
  }
  const ThemeIcon = theme.Icon

  return (
    <div className="app-layout">
      <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <motion.aside
        className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}
        variants={sidebarVariants}
        animate={sidebarOpen ? 'open' : 'closed'}
        initial="closed"
        style={{ width: sidebarOpen ? 280 : 80 }}
      >
        <div className="sidebar-header">
          <motion.div className="logo" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => navigate('/')}>
            <div className="logo-with-status">
              <img src={logoSrc} onError={onLogoError} alt={brandName} className="logo-img" style={{ maxWidth: sidebarOpen ? 120 : 44 }} />
              <SyncDot sync={sync} />
            </div>
            <AnimatePresence>
              {sidebarOpen && (
                <motion.span className="logo-text" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}>
                  {brandName}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>

          {sidebarOpen && (
            <motion.button className="collapse-btn" onClick={() => setSidebarOpen(false)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              title="Collapse sidebar" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <PanelLeftClose size={20} />
            </motion.button>
          )}
        </div>

        {!sidebarOpen && (
          <motion.div className="sidebar-toggle-collapsed" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.button className="collapse-btn centered" onClick={() => setSidebarOpen(true)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} title="Expand sidebar">
              <PanelLeftOpen size={20} />
            </motion.button>
          </motion.div>
        )}

        <nav className="sidebar-nav">
          {navItems.map((item, index) => (
            <NavLink key={item.path} to={item.path} end={item.exact}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
              title={!sidebarOpen ? item.label : undefined}>
              {({ isActive }) => (
                <motion.div className="nav-item-content" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }} whileHover={{ x: 4 }}>
                  {isActive && <motion.div className="nav-indicator" layoutId="nav-indicator" transition={{ type: 'spring', stiffness: 500, damping: 30 }} />}
                  <item.icon size={20} className="nav-icon" />
                  <motion.span className="nav-label" variants={labelVariants}>{item.label}</motion.span>
                </motion.div>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <motion.div className="user-avatar" whileHover={{ scale: 1.05 }}>{user.initials}</motion.div>
            <motion.div className="user-details" variants={labelVariants}>
              <span className="user-name">{user.name}</span>
              {user.sub != null && <span className={`user-role ${user.subClass || ''}`}>{user.sub}</span>}
            </motion.div>
          </div>

          <motion.button className="theme-toggle-btn" onClick={theme.cycle} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            title={!sidebarOpen ? `Theme: ${theme.label}` : undefined}>
            <ThemeIcon size={20} />
            <motion.span variants={labelVariants}>{theme.label}</motion.span>
          </motion.button>

          <motion.button className="logout-btn" onClick={onLogout} whileHover={{ scale: 1.02, backgroundColor: 'rgba(239, 68, 68, 0.15)' }} whileTap={{ scale: 0.98 }}
            title={!sidebarOpen ? 'Sign Out' : undefined}>
            <LogOut size={20} />
            <motion.span variants={labelVariants}>Sign Out</motion.span>
          </motion.button>
        </div>
      </motion.aside>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div className="mobile-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileMenuOpen(false)} />
        )}
      </AnimatePresence>

      <motion.main className="main-content" initial={{ marginLeft: 80 }} animate={{ marginLeft: sidebarOpen ? 280 : 80 }} transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}>
        <div className="page-container">{children}</div>
      </motion.main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// topbar — brand + horizontal nav + actions in one bar across the top
// ---------------------------------------------------------------------------
function TopbarShell({ navItems, brandName, logoSrc, onLogoError, user, theme, sync, onLogout, children }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="pl-topbar">
      <header className="pl-topbar-bar">
        <NavLink to="/" className="pl-brand">
          <img src={logoSrc} onError={onLogoError} alt="" className="pl-brand-logo" />
          <span className="pl-brand-name">{brandName}</span>
          <SyncDot sync={sync} />
        </NavLink>
        <button type="button" className="pl-icon-btn pl-only-mobile" onClick={() => setOpen(o => !o)} aria-label="Menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
        <nav className={`pl-topbar-nav ${open ? 'open' : ''}`}>
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.exact} onClick={() => setOpen(false)}
              className={({ isActive }) => `pl-topbar-link ${isActive ? 'active' : ''}`}>
              <item.icon size={16} /><span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="pl-actions">
          <div className="pl-user">
            <span className="pl-avatar">{user.initials}</span>
            <span className="pl-user-text">
              <span className="pl-user-name">{user.name}</span>
              {user.sub != null && <span className={`pl-user-sub ${user.subClass || ''}`}>{user.sub}</span>}
            </span>
          </div>
          <ThemeButton theme={theme} className="pl-icon-btn" showLabel={false} />
          <LogoutButton onLogout={onLogout} className="pl-icon-btn pl-danger" showLabel={false} />
        </div>
      </header>
      <main className="pl-topbar-main"><div className="page-container">{children}</div></main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// rail — 72 px icon rail; header bar shows the current page + user menu
// ---------------------------------------------------------------------------
function RailShell({ navItems, brandName, logoSrc, onLogoError, user, theme, sync, onLogout, children }) {
  const current = useCurrentLabel(navItems)
  return (
    <div className="pl-rail">
      <aside className="pl-rail-bar">
        <NavLink to="/" className="pl-rail-logo" title={brandName}>
          <img src={logoSrc} onError={onLogoError} alt="" />
          <SyncDot sync={sync} />
        </NavLink>
        <nav className="pl-rail-nav">
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.exact} title={item.label}
              className={({ isActive }) => `pl-rail-link ${isActive ? 'active' : ''}`}>
              <item.icon size={20} />
              <span className="pl-rail-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="pl-rail-foot">
          <ThemeButton theme={theme} className="pl-icon-btn" showLabel={false} />
          <LogoutButton onLogout={onLogout} className="pl-icon-btn pl-danger" showLabel={false} />
        </div>
      </aside>
      <div className="pl-rail-body">
        <header className="pl-rail-header">
          <div className="pl-rail-title">
            <span className="pl-rail-brand">{brandName}</span>
            {current && <><span className="pl-rail-sep">/</span><span>{current}</span></>}
          </div>
          <div className="pl-user">
            <span className="pl-user-text pl-align-right">
              <span className="pl-user-name">{user.name}</span>
              {user.sub != null && <span className={`pl-user-sub ${user.subClass || ''}`}>{user.sub}</span>}
            </span>
            <span className="pl-avatar">{user.initials}</span>
            {/* the rail's own buttons are hidden when it becomes a bottom bar */}
            <span className="pl-actions pl-only-mobile">
              <ThemeButton theme={theme} className="pl-icon-btn" showLabel={false} />
              <LogoutButton onLogout={onLogout} className="pl-icon-btn pl-danger" showLabel={false} />
            </span>
          </div>
        </header>
        <main className="pl-rail-main"><div className="page-container">{children}</div></main>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// panel — fixed sidebar filled with the brand colour, white type
// ---------------------------------------------------------------------------
function PanelShell({ navItems, brandName, logoSrc, onLogoError, user, theme, sync, onLogout, children }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="pl-panel">
      <header className="pl-panel-mobilebar pl-only-mobile">
        <button type="button" className="pl-icon-btn" onClick={() => setOpen(o => !o)} aria-label="Menu">{open ? <X size={20} /> : <Menu size={20} />}</button>
        <span className="pl-brand-name">{brandName}</span>
      </header>
      <aside className={`pl-panel-side ${open ? 'open' : ''}`}>
        <NavLink to="/" className="pl-panel-brand" onClick={() => setOpen(false)}>
          <span className="pl-panel-logo"><img src={logoSrc} onError={onLogoError} alt="" /></span>
          <span className="pl-panel-name">{brandName}</span>
          <SyncDot sync={sync} />
        </NavLink>
        <nav className="pl-panel-nav">
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.exact} onClick={() => setOpen(false)}
              className={({ isActive }) => `pl-panel-link ${isActive ? 'active' : ''}`}>
              <item.icon size={18} /><span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="pl-panel-foot">
          <div className="pl-panel-user">
            <span className="pl-avatar pl-avatar-light">{user.initials}</span>
            <span className="pl-user-text">
              <span className="pl-user-name">{user.name}</span>
              {user.sub != null && <span className="pl-user-sub">{user.sub}</span>}
            </span>
          </div>
          <div className="pl-panel-actions">
            <ThemeButton theme={theme} className="pl-panel-btn" />
            <LogoutButton onLogout={onLogout} className="pl-panel-btn" />
          </div>
        </div>
      </aside>
      {open && <div className="pl-scrim pl-only-mobile" onClick={() => setOpen(false)} />}
      <main className="pl-panel-main"><div className="page-container">{children}</div></main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// minimal — centred column, brand on top, pill tabs
// ---------------------------------------------------------------------------
function MinimalShell({ navItems, brandName, logoSrc, onLogoError, user, theme, sync, onLogout, children }) {
  return (
    <div className="pl-minimal">
      <header className="pl-minimal-head">
        <div className="pl-minimal-row">
          <NavLink to="/" className="pl-brand">
            <img src={logoSrc} onError={onLogoError} alt="" className="pl-brand-logo" />
            <span className="pl-brand-name">{brandName}</span>
            <SyncDot sync={sync} />
          </NavLink>
          <div className="pl-actions">
            <span className="pl-user-text pl-align-right">
              <span className="pl-user-name">{user.name}</span>
              {user.sub != null && <span className={`pl-user-sub ${user.subClass || ''}`}>{user.sub}</span>}
            </span>
            <ThemeButton theme={theme} className="pl-icon-btn" showLabel={false} />
            <LogoutButton onLogout={onLogout} className="pl-icon-btn pl-danger" showLabel={false} />
          </div>
        </div>
        <nav className="pl-minimal-tabs">
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.exact}
              className={({ isActive }) => `pl-pill ${isActive ? 'active' : ''}`}>
              <item.icon size={15} /><span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="pl-minimal-main"><div className="page-container">{children}</div></main>
    </div>
  )
}

const SHELLS = { classic: ClassicShell, topbar: TopbarShell, rail: RailShell, panel: PanelShell, minimal: MinimalShell }
